import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Printer, Pencil, Eye, Copy, Save, UtensilsCrossed, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { downloadPdf } from "../lib/pdf";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const DAYS = { fr: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"], nl: ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"], en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] };
const MONTHS = { fr: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"], nl: ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"], en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] };
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (d) => { const x = new Date(d); const dow = x.getDay(); x.setDate(x.getDate() + (dow === 0 ? -6 : 1 - dow)); x.setHours(0, 0, 0, 0); return x; };
const BERRY = "#be185d", BERRY_DARK = "#9d174d", CREAM = "#fffaf3";

const emptyDay = () => ({ diner_entree: "", diner_plat: "", diner_dessert: "", diner_allerg: "", souper_entree: "", souper_plat: "", souper_dessert: "", souper_allerg: "" });
const emptyMenu = () => ({ days: Array.from({ length: 7 }, emptyDay), gouter: "", collation: "", remplacement_midi: "", remplacement_soir: "", note: "" });

function Sep() {
  return <div style={{ color: "#ec4899", letterSpacing: 4, fontSize: 11, margin: "4px 0" }}>❋ ❋ ❋</div>;
}

export default function MenuSemaine() {
  const { i18n, t } = useTranslation();
  const lng = (i18n.language || "fr").slice(0, 2);
  const L = DAYS[lng] || DAYS.fr, ML = MONTHS[lng] || MONTHS.fr;
  const [monday, setMonday] = useState(mondayOf(new Date()));
  const [menu, setMenu] = useState(emptyMenu());
  const [canEdit, setCanEdit] = useState(false);
  const [exists, setExists] = useState(false);
  const [maison, setMaison] = useState("");
  const [mode, setMode] = useState("view");
  const [dishes, setDishes] = useState([]);
  useEffect(() => { api.get("/cuisine/dishes").then(({ data }) => setDishes(data.dishes || [])).catch(() => { }); }, []);
  const allergOf = (name) => (dishes.find((x) => x.name.trim().toLowerCase() === String(name || "").trim().toLowerCase())?.allergens || "");
  const setDish = (i, meal, field, value) => {
    setMenu((m) => {
      const days = m.days.map((d, j) => (j === i ? { ...d, [`${meal}_${field}`]: value } : d));
      const day = days[i];
      const parts = [day[`${meal}_entree`], ...String(day[`${meal}_plat`] || "").split("\n"), day[`${meal}_dessert`]];
      const nums = new Set();
      parts.forEach((p) => allergOf(p).split("/").filter(Boolean).forEach((n) => nums.add(Number(n))));
      if (nums.size) days[i] = { ...days[i], [`${meal}_allerg`]: [...nums].sort((a, b) => a - b).join("/") };
      return { ...m, days };
    });
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (mon) => {
    setLoading(true);
    try {
      const { data } = await api.get("/cuisine/menu", { params: { week_start: iso(mon) } });
      setMenu({ ...emptyMenu(), ...data.menu, days: (data.menu.days || []).map((d) => ({ ...emptyDay(), ...d })) });
      setCanEdit(!!data.can_edit); setExists(!!data.exists);
      if (data.maison) setMaison(data.maison);
    } catch { setMenu(emptyMenu()); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(monday); }, [monday, load]);

  const shift = (days) => { const d = new Date(monday); d.setDate(d.getDate() + days); setMonday(mondayOf(d)); };
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const titleFrom = `${L[0].toLowerCase()} ${pad(monday.getDate())} ${ML[monday.getMonth()]}`;
  const titleTo = `${L[6].toLowerCase()} ${pad(sunday.getDate())} ${ML[sunday.getMonth()]} ${sunday.getFullYear()}`;

  const setDay = (i, field, value) => setMenu((m) => ({ ...m, days: m.days.map((d, j) => (j === i ? { ...d, [field]: value } : d)) }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/cuisine/menu", { week_start: iso(monday), ...menu });
      toast.success(t("cuisine.menuSaved")); setExists(true); setMode("view");
    } catch (e) { toast.error(e?.response?.data?.detail || t("cuisine.saveFailed")); }
    finally { setSaving(false); }
  };
  const copyPrev = async () => {
    const prev = new Date(monday); prev.setDate(prev.getDate() - 7);
    try {
      const { data } = await api.post("/cuisine/menu/copy", { from_week: iso(prev), to_week: iso(monday) });
      setMenu({ ...emptyMenu(), ...data.menu, days: (data.menu.days || []).map((d) => ({ ...emptyDay(), ...d })) });
      setExists(true);
      toast.success(t("cuisine.menuCopied"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("cuisine.saveFailed")); }
  };

  const allergLegend = Array.from({ length: 14 }, (_, i) => `${i + 1} – ${t(`cuisine.allerg${i + 1}`)}`);
  const dishStyle = { whiteSpace: "pre-wrap", fontWeight: 600, color: "#3f3247", lineHeight: 1.35 };

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="menu-semaine">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600;700&family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500&display=swap');
        .menu-script { font-family: 'Dancing Script', cursive; }
        .menu-serif { font-family: 'Cormorant Garamond', Georgia, serif; }
        @media print {
          @page { size: A4 landscape; margin: 6mm; }
          body * { visibility: hidden !important; }
          #menu-print, #menu-print * { visibility: visible !important; }
          #menu-print { display: block !important; position: fixed; left: 0; top: 0; width: 100%; margin: 0; border-radius: 0 !important; box-shadow: none !important; }
        }
      `}</style>

      <div className="rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden no-print" style={{ background: "linear-gradient(135deg, #500724 0%, #9d174d 55%, #db2777 100%)" }}>
        <div className="absolute -right-10 -top-10 h-52 w-52 rounded-full" style={{ background: "radial-gradient(circle, rgba(251,207,232,0.28), transparent 70%)" }} />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-white/60 flex items-center gap-1.5"><UtensilsCrossed className="h-3.5 w-3.5" /> {maison || t("cuisine.title")}</div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">{t("nav.menuSemaine")}</h1>
            <p className="text-white/70 text-sm mt-1">{t("cuisine.menuSubtitle")}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-0" data-testid="menu-prev" onClick={() => shift(-7)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-0" data-testid="menu-today" onClick={() => setMonday(mondayOf(new Date()))}>{t("planning.today")}</Button>
            <Button size="sm" variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-0" data-testid="menu-next" onClick={() => shift(7)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="relative flex items-center gap-2 mt-5 flex-wrap">
          {canEdit && mode === "view" && (
            <Button size="sm" className="bg-white text-pink-700 hover:bg-pink-50 rounded-full" data-testid="menu-edit-btn" onClick={() => setMode("edit")}><Pencil className="h-4 w-4 mr-1.5" /> {t("cuisine.editMenu")}</Button>
          )}
          {canEdit && mode === "edit" && (
            <>
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full" data-testid="menu-save-btn" onClick={save} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {t("cuisine.saveMenu")}
              </Button>
              <Button size="sm" variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-0 rounded-full" data-testid="menu-view-btn" onClick={() => setMode("view")}><Eye className="h-4 w-4 mr-1.5" /> {t("cuisine.viewMenu")}</Button>
              <Button size="sm" variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-0 rounded-full" data-testid="menu-copy-prev" onClick={copyPrev}><Copy className="h-4 w-4 mr-1.5" /> {t("cuisine.copyPrevWeek")}</Button>
            </>
          )}
          <Button size="sm" variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-0 rounded-full" data-testid="menu-print-btn" onClick={() => window.print()}><Printer className="h-4 w-4 mr-1.5" /> {t("cuisine.print")}</Button>
          {mode === "view" && exists && <Button size="sm" className="bg-white text-pink-700 hover:bg-pink-50 rounded-full" data-testid="menu-pdf-btn" onClick={() => downloadPdf("menu-print", `menu-semaine-${iso(monday)}.pdf`, true)}>⬇ PDF</Button>}
          {!canEdit && <span className="text-white/70 text-xs ml-1">{t("cuisine.readOnly")}</span>}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-pink-400" /></div>
      ) : mode === "edit" ? (
        <div className="space-y-4 no-print" data-testid="menu-editor">
          {["diner", "souper"].map((meal) => ["entree", "plat", "dessert"].map((course) => (
            <datalist key={`${meal}-${course}`} id={`dl-${meal}-${course}`}>
              {dishes.filter((d) => d.meal === meal && d.course === course).map((d) => <option key={d.id} value={d.name}>{d.allergens ? `Allergènes ${d.allergens}` : ""}</option>)}
            </datalist>
          )))}
          {L.map((d, i) => {
            const dt = new Date(monday); dt.setDate(monday.getDate() + i);
            const day = menu.days[i];
            return (
              <Card key={i} className="p-4 glass">
                <h3 className="font-display font-bold text-pink-300 mb-3">{d} {pad(dt.getDate())}/{pad(dt.getMonth() + 1)}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {["diner", "souper"].map((meal) => (
                    <div key={meal} className="rounded-xl ring-1 ring-white/10 p-3">
                      <div className="text-xs font-bold uppercase tracking-wide text-pink-400 mb-2">{t(`cuisine.${meal}`)}</div>
                      <div className="space-y-2">
                        <div><Label className="text-xs">{t("cuisine.entree")}</Label>
                          <Input data-testid={`menu-${meal}-entree-${i}`} list={`dl-${meal}-entree`} value={day[`${meal}_entree`]} onChange={(e) => setDish(i, meal, "entree", e.target.value)} className="mt-0.5 h-9" /></div>
                        <div><Label className="text-xs">{t("cuisine.plat")}</Label>
                          <Input data-testid={`menu-${meal}-plat-pick-${i}`} list={`dl-${meal}-plat`} placeholder={t("cuisine.pickDish")} value="" onChange={(e) => { const v = e.target.value; if (allergOf(v)) setDish(i, meal, "plat", (day[`${meal}_plat`] ? day[`${meal}_plat`] + "\n" : "") + v); }} className="mt-0.5 h-8 text-xs opacity-80" />
                          <textarea data-testid={`menu-${meal}-plat-${i}`} value={day[`${meal}_plat`]} onChange={(e) => setDish(i, meal, "plat", e.target.value)} rows={2} className="mt-1 w-full rounded-md border px-3 py-1.5 bg-background text-sm" /></div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><Label className="text-xs">{t("cuisine.dessert")}</Label>
                            <Input data-testid={`menu-${meal}-dessert-${i}`} list={`dl-${meal}-dessert`} value={day[`${meal}_dessert`]} onChange={(e) => setDish(i, meal, "dessert", e.target.value)} className="mt-0.5 h-9" /></div>
                          <div><Label className="text-xs">{t("cuisine.allergens")}</Label>
                            <Input data-testid={`menu-${meal}-allerg-${i}`} value={day[`${meal}_allerg`]} onChange={(e) => setDay(i, `${meal}_allerg`, e.target.value)} placeholder="1/5/8/9" className="mt-0.5 h-9" /></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
          <Card className="p-4 glass">
            <h3 className="font-display font-bold text-pink-300 mb-3">{t("cuisine.weekInfos")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("cuisine.gouter")}</Label>
                <Input data-testid="menu-gouter" value={menu.gouter} onChange={(e) => setMenu({ ...menu, gouter: e.target.value })} placeholder={t("cuisine.gouterPlaceholder")} className="mt-0.5" /></div>
              <div><Label className="text-xs">{t("cuisine.collation")}</Label>
                <Input data-testid="menu-collation" value={menu.collation} onChange={(e) => setMenu({ ...menu, collation: e.target.value })} placeholder={t("cuisine.collationPlaceholder")} className="mt-0.5" /></div>
              <div><Label className="text-xs">{t("cuisine.replacement")} — {t("cuisine.replacementMidi")}</Label>
                <Input data-testid="menu-remplacement-midi" value={menu.remplacement_midi} onChange={(e) => setMenu({ ...menu, remplacement_midi: e.target.value })} className="mt-0.5" /></div>
              <div><Label className="text-xs">{t("cuisine.replacement")} — {t("cuisine.replacementSoir")}</Label>
                <Input data-testid="menu-remplacement-soir" value={menu.remplacement_soir} onChange={(e) => setMenu({ ...menu, remplacement_soir: e.target.value })} className="mt-0.5" /></div>
              <div className="md:col-span-2"><Label className="text-xs">{t("cuisine.menuNote")}</Label>
                <textarea data-testid="menu-note" value={menu.note} onChange={(e) => setMenu({ ...menu, note: e.target.value })} rows={2} className="mt-0.5 w-full rounded-md border px-3 py-1.5 bg-background text-sm" placeholder={t("cuisine.menuNotePlaceholder")} /></div>
            </div>
            <Button data-testid="menu-save-btn-bottom" onClick={save} disabled={saving} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {t("cuisine.saveMenu")}
            </Button>
          </Card>
        </div>
      ) : (
        <div id="menu-print" className="rounded-3xl shadow-2xl overflow-hidden" style={{ background: CREAM }} data-testid="menu-view">
          <div className="px-5 sm:px-8 pt-6 pb-4 text-center">
            <h2 className="menu-script" style={{ color: BERRY, fontSize: "clamp(26px, 4vw, 44px)", lineHeight: 1.1 }} data-testid="menu-title">
              {t("cuisine.menuWeekTitle")} {titleFrom} {t("cuisine.menuWeekTo")} {titleTo}
            </h2>
            <div className="mx-auto mt-2 h-[3px] w-40 rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${BERRY}, transparent)` }} />
          </div>

          {!exists && (
            <div className="text-center pb-8">
              <p className="menu-serif" style={{ color: "#a3919b", fontSize: 18 }} data-testid="menu-empty">{t("cuisine.emptyMenu")}</p>
              {canEdit && <Button className="mt-3 bg-pink-700 hover:bg-pink-800 text-white rounded-full px-6" data-testid="menu-add-btn" onClick={() => setMode("edit")}><Pencil className="h-4 w-4 mr-1.5" /> {t("cuisine.addMenuBtn")}</Button>}
            </div>
          )}

          {exists && (
            <div className="px-3 sm:px-6 pb-6">
              <div className="grid" style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4 }}>
                {L.map((d, i) => {
                  const dt = new Date(monday); dt.setDate(monday.getDate() + i);
                  return (
                    <div key={i} className="text-center rounded-t-xl py-2 text-white menu-serif" style={{ background: `linear-gradient(160deg, ${BERRY}, ${BERRY_DARK})`, fontWeight: 700, fontSize: "clamp(11px, 1.2vw, 16px)" }}>
                      {d}<div style={{ fontSize: "0.75em", opacity: 0.85, fontWeight: 500 }}>{pad(dt.getDate())}/{pad(dt.getMonth() + 1)}</div>
                    </div>
                  );
                })}
                {menu.days.map((day, i) => (
                  <div key={`din-${i}`} className="text-center menu-serif bg-white rounded-b-none p-2" style={{ border: "1px solid #f3d3e2", fontSize: "clamp(10px, 1.05vw, 14px)", minHeight: 150 }} data-testid={`menu-day-diner-${i}`}>
                    <div style={{ color: BERRY, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, fontSize: "0.8em", marginBottom: 4 }}>{t("cuisine.diner")}</div>
                    {day.diner_entree && <div style={dishStyle}>{day.diner_entree}</div>}
                    {day.diner_entree && (day.diner_plat || day.diner_dessert) && <Sep />}
                    {day.diner_plat && <div style={dishStyle}>{day.diner_plat}</div>}
                    {day.diner_plat && day.diner_dessert && <Sep />}
                    {day.diner_dessert && <div style={dishStyle}>{day.diner_dessert}</div>}
                    {day.diner_allerg && <div style={{ color: "#d1548f", fontSize: "0.8em", marginTop: 4, fontWeight: 600 }}>{day.diner_allerg}</div>}
                  </div>
                ))}
                <div style={{ gridColumn: "1 / -1", background: "#fce7f1", border: "1px solid #f3d3e2", padding: "5px 10px", textAlign: "center", color: BERRY_DARK, fontWeight: 700 }} className="menu-serif">
                  {t("cuisine.gouter")}{menu.gouter ? ` — ${menu.gouter}` : ""}
                </div>
                {menu.days.map((day, i) => (
                  <div key={`sou-${i}`} className="text-center menu-serif bg-white p-2" style={{ border: "1px solid #f3d3e2", fontSize: "clamp(10px, 1.05vw, 14px)", minHeight: 120 }} data-testid={`menu-day-souper-${i}`}>
                    <div style={{ color: BERRY, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, fontSize: "0.8em", marginBottom: 4 }}>{t("cuisine.souper")}</div>
                    {day.souper_entree && <div style={dishStyle}>{day.souper_entree}</div>}
                    {day.souper_entree && (day.souper_plat || day.souper_dessert) && <Sep />}
                    {day.souper_plat && <div style={dishStyle}>{day.souper_plat}</div>}
                    {day.souper_plat && day.souper_dessert && <Sep />}
                    {day.souper_dessert && <div style={dishStyle}>{day.souper_dessert}</div>}
                    {day.souper_allerg && <div style={{ color: "#d1548f", fontSize: "0.8em", marginTop: 4, fontWeight: 600 }}>{day.souper_allerg}</div>}
                  </div>
                ))}
                {menu.collation && (
                  <div style={{ gridColumn: "1 / -1", background: "#fce7f1", border: "1px solid #f3d3e2", padding: "5px 10px", textAlign: "center", color: BERRY_DARK, fontWeight: 700 }} className="menu-serif" data-testid="menu-collation-band">
                    {menu.collation}
                  </div>
                )}
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 menu-serif" style={{ color: "#5b4a55", fontSize: 13 }}>
                <div>
                  {(menu.remplacement_midi || menu.remplacement_soir) && (
                    <div className="mb-2" data-testid="menu-replacement">
                      <span style={{ color: BERRY, fontWeight: 700, textDecoration: "underline" }}>{t("cuisine.replacement")} :</span>
                      {menu.remplacement_midi && <div><b>{t("cuisine.replacementMidi")} :</b> {menu.remplacement_midi}</div>}
                      {menu.remplacement_soir && <div><b>{t("cuisine.replacementSoir")} :</b> {menu.remplacement_soir}</div>}
                    </div>
                  )}
                  {menu.note && <div style={{ color: "#b3436e" }}>/!\ {menu.note}</div>}
                </div>
                <div>
                  <span style={{ color: BERRY, fontWeight: 700, textDecoration: "underline" }}>{t("cuisine.allergensLegend")} :</span>
                  <div className="grid grid-cols-2 gap-x-3" style={{ fontSize: 11.5, marginTop: 2 }}>
                    {allergLegend.map((a) => <span key={a}>{a}</span>)}
                  </div>
                </div>
              </div>

              <div className="mt-5 flex items-end justify-between flex-wrap gap-2 px-1 pb-2">
                <div className="menu-serif" style={{ color: "#8a7580", fontSize: 12 }}>{maison}</div>
                <div className="text-right">
                  <div className="menu-script" style={{ color: "#ec4899", fontSize: 26, lineHeight: 1 }}>{t("cuisine.bonAppetit")}</div>
                  <div className="menu-serif" style={{ color: "#5b4a55", fontSize: 15 }}>{t("cuisine.chefTeam")}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
